import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  archiveProject,
  createProject,
  duplicateProject,
  listProjects,
} from '../api/client';
import BrandLogo from '../components/BrandLogo';
import UserMenu from '../components/UserMenu';
import Spinner from '../components/ui/Spinner';
import { APP_VERSION } from '../lib/appVersion';
import { useAuthStore } from '../store/authStore';
import type { ProjectSummary } from '../types/project';

// ------------------------------------------------------------
// Page: Project library
// ------------------------------------------------------------

function formatRelativeDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export default function ProjectLibraryPage() {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listProjects();
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim() || 'Untitled Project';
    setCreating(true);
    setError(null);
    try {
      await createProject(name);
      setNewName('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateProject(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate');
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveProject(id, true);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive');
    }
  };

  return (
    <div className="min-h-full overflow-auto bg-surface">
      <header className="border-b border-border bg-surface-raised/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <BrandLogo variant="full" linkToHome />
          <div className="flex items-center gap-3">
            <span className="rounded-full border border-border bg-surface-overlay px-3 py-1 text-xs text-text-subtle">
              Simulation Designer
            </span>
            <span
              className="rounded-full border border-border bg-surface-overlay px-2.5 py-1 font-mono text-[11px] text-text-subtle"
              title="Application version"
            >
              v{APP_VERSION}
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <section className="mb-10 animate-slide-up">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-surface-raised via-surface-raised to-kromeka-red/10 p-8 shadow-card md:p-10">
            <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-kromeka-red/10 blur-3xl" />
            <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <h1 className="text-2xl font-bold tracking-tight text-text md:text-3xl">
                  Design your production flow
                </h1>
                <p className="mt-3 text-text-muted leading-relaxed">
                  Build discrete-event simulation layouts with sources, processors,
                  and routes — then refine and share your models with the team.
                </p>
              </div>
              {isAdmin && (
                <form
                  onSubmit={handleCreate}
                  className="flex w-full shrink-0 flex-col gap-3 sm:flex-row md:max-w-md"
                >
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="New project name…"
                    className="input-field flex-1"
                  />
                  <button
                    type="submit"
                    disabled={creating}
                    className="btn-primary shrink-0 px-6 shadow-brand"
                  >
                    {creating ? 'Creating…' : 'New Project'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {error && (
          <div className="alert-danger mb-6 animate-fade-in px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <section>
          <div className="mb-5 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-text">Your projects</h2>
            {!loading && (
              <span className="text-sm text-text-subtle">
                {projects.length} project{projects.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner label="Loading projects…" />
            </div>
          ) : projects.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-surface-raised/50 px-8 py-16 text-center">
              <img
                src="/kromeka-o.png"
                alt=""
                className="mx-auto mb-4 h-16 w-16 opacity-60"
              />
              <p className="text-lg font-medium text-text-muted">
                No projects yet
              </p>
              <p className="mx-auto mt-2 max-w-sm text-sm text-text-subtle">
                Create your first simulation layout above — map sources, sinks,
                and processing steps on the canvas.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <article
                  key={project.id}
                  className="group flex flex-col rounded-xl border border-border bg-surface-raised p-5 shadow-card transition hover:border-kromeka-gray/50 hover:shadow-elevated"
                >
                  <Link
                    to={`/projects/${project.id}`}
                    className="min-w-0 flex-1"
                  >
                    <h3 className="truncate font-semibold text-text transition group-hover:text-accent-on-dark">
                      {project.name}
                    </h3>
                    {project.description ? (
                      <p className="mt-1 line-clamp-2 text-sm text-text-subtle">
                        {project.description}
                      </p>
                    ) : (
                      <p className="mt-1 text-sm italic text-text-subtle">
                        No description
                      </p>
                    )}
                    <p className="mt-3 text-xs text-text-subtle">
                      Updated {formatRelativeDate(project.updated_at)} · v
                      {project.version_number}
                    </p>
                  </Link>
                  <div className="mt-4 flex gap-2 border-t border-border-subtle pt-4">
                    <Link
                      to={`/projects/${project.id}`}
                      className="btn-primary flex-1 py-1.5 text-xs"
                    >
                      Open
                    </Link>
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleDuplicate(project.id)}
                          className="btn-ghost px-3 py-1.5 text-xs"
                          title="Duplicate"
                        >
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleArchive(project.id)}
                          className="btn-danger-ghost px-3 py-1.5 text-xs"
                          title="Archive"
                        >
                          Archive
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
